<template>
  <section :class="$style.container">
    <SettingsTable :class="$style.table" :isEditing="true">
      <AnswerMessageAutoChatReplySettingTableRow name="ステータス">
        <template #editor>
          <div>
            <!-- negative margin guard -->
            <ul :class="$style.options">
              <li
                :class="$style.options__item"
                :key="index"
                v-for="(option, index) in isEnabledOptions"
              >
                <label :class="$style.option">
                  <DolphinRadioButton
                    name="isEnabled"
                    v-model="formRef.isEnabled"
                    :value="option.value"
                  />
                  <span :class="$style.option__text">
                    {{ option.text }}
                  </span>
                </label>
              </li>
            </ul>
          </div>
        </template>
      </AnswerMessageAutoChatReplySettingTableRow>
      <AnswerMessageAutoChatReplySettingTableRow name="自動応答する選択肢">
        <template #editor>
          <div>
            <DolphinTextInput
              type="text"
              v-model="formRef.keyword"
              :class="$style.textInput"
              :invalid="!!formErrors.keyword"
            />
            <p :class="$style.error">{{ formErrors.keyword }}</p>
            <p :class="[$style.row__info, $style.explanation]">
              ※完全一致する選択肢を受信したときにのみ自動で返信します。
            </p>
          </div>
        </template>
      </AnswerMessageAutoChatReplySettingTableRow>
      <AnswerMessageAutoChatReplySettingTableRow name="メッセージ内容">
        <template #editor>
          <DolphinTextarea
            :rows="8"
            v-model="formRef.body"
            :invalid="!!formErrors.body"
          />
          <p :class="$style.error">{{ formErrors.body }}</p>
        </template>
      </AnswerMessageAutoChatReplySettingTableRow>
      <AnswerMessageAutoChatReplySettingTableRow name="">
        <template #editor>
          <div style="font-size: 16px; font-weight: bold; margin: 16px 0">
            メッセージに添付する内容
          </div>
          <ul :class="$style.options">
            <li
              :class="$style.options__item"
              :key="index"
              v-for="(option, index) in extraOptions"
            >
              <label :class="$style.option">
                <DolphinRadioButton
                  name="extraType"
                  :model-value="formRef.extra.type"
                  @update:model-value="updateExtra"
                  :value="option.value"
                />
                <span :class="$style.option__text">
                  {{ option.text }}
                </span>
              </label>
            </li>
          </ul>

          <div
            v-if="formRef.extra.type === AutoChatReplySettingExtraType.Question"
            :class="$style.extra"
          >
            <MessageAttachmentActions
              v-model="formRef.extra.selections"
              :isDeleteAllSelectionsAllowed="false"
            />
            <p :class="$style.error">{{ formErrors.extraSelections }}</p>
          </div>
          <div
            v-else-if="
              formRef.extra.type ===
              AutoChatReplySettingExtraType.BookingRequest
            "
            :class="$style.extra"
          >
            <div :class="$style.bookingRequest">
              <p
                v-if="!isCalendarAvailable"
                :class="$style.bookingRequest__warning"
              >
                外部カレンダー連携をしているメンバーがいないので日程調整ができません。
                <RouterLink :to="{ name: 'calendarSetting' }" target="_blank"
                  >カレンダー連携設定ページ</RouterLink
                >
                で連携をしてください。
              </p>

              <div :class="$style.bookingRequest__label">
                使用する日程調整カレンダー
              </div>
              <template v-if="calendarOptions.length === 0">
                <p :class="$style.error">
                  公開中の日程調整カレンダーがありません。<br />
                  <router-link :to="{ name: 'calendarSetting' }"
                    >カレンダー連携設定</router-link
                  >
                  から日程調整カレンダーを作成、または公開ステータスにしてください。
                </p>
              </template>
              <template v-else>
                <div :class="$style.calendarSelector">
                  <DolphinSelector
                    :class="$style.bookingRequest__selectInput"
                    :options="calendarOptions"
                    v-model="formRef.extra.assignedCalendarGuid"
                    :invalid="!!formErrors.extraAssignedCalendarGuid"
                  />
                  <ButtonIconOnly
                    v-if="formRef.extra.assignedCalendarGuid"
                    :class="$style.deleteButton"
                    @click="clearCalendarSelection"
                  >
                    <template #icon><IconTrash /></template>
                  </ButtonIconOnly>
                </div>
                <div :class="$style.bookingRequest__note">
                  <span>
                    未設定の場合、
                    <router-link :to="{ name: 'calendarSetting' }"
                      >カレンダー連携設定</router-link
                    >
                    の設定に従います
                  </span>
                </div>
              </template>

              <div :class="$style.bookingRequest__label">
                <span>送信する日程の種類</span>
              </div>
              <ul
                :class="[$style.options, $style['options--bookingRequest']]"
                v-for="(
                  optionGroup, i
                ) in bookingRequestAvailableTimeSlotRangeOptionGroups"
                :key="i"
              >
                <li
                  :class="$style.options__item"
                  v-for="(option, j) in optionGroup"
                  :key="j"
                >
                  <label :class="$style.option">
                    <DolphinRadioButton
                      name="availableTimeSlotRange"
                      :model-value="formRef.extra.availableTimeSlotRange"
                      @update:model-value="
                        updateBookingRequestAvailableTimeSlotRange
                      "
                      :value="option.value"
                    />
                    <span :class="$style.option__text">
                      {{ option.text }}
                    </span>
                  </label>
                </li>
              </ul>
              <p :class="$style.error">
                {{ formErrors.extraAvailableTimeSlotRange }}
              </p>

              <div :class="$style.bookingRequest__label">
                <span>外部カレンダー URL（任意）</span>
              </div>
              <DolphinTextInput
                :class="$style.bookingRequest__input"
                v-model="formRef.extra.fallbackUrl"
                :invalid="!!formErrors.extraFallbackUrl"
              />
              <div :class="$style.bookingRequest__note">
                <span>
                  ここに URL
                  を入力すると、「別の日程を選ぶ」がオカネコカレンダーではなく、入力された
                  URL が開きます
                </span>
              </div>
              <p :class="$style.error">{{ formErrors.extraFallbackUrl }}</p>
              <AnswerMessageAutoChatReplySettingTableInformation
                :class="$style.bookingRequest__info"
              />
            </div>
          </div>
        </template>
      </AnswerMessageAutoChatReplySettingTableRow>
    </SettingsTable>
    <div :class="$style.saveWrapper">
      <FlatButton size="large" @click="saveSetting" :disabled="submitting"
        >この内容で保存する</FlatButton
      >
    </div>
  </section>
</template>

<script lang="ts">
type Extra =
  | {
      type: typeof AutoChatReplySettingExtraType.Question
      selections: string[]
    }
  | {
      type: typeof AutoChatReplySettingExtraType.BookingRequest
      availableTimeSlotRange: ExtraBookingRequestAvailableTimeSlotRange
      fallbackUrl: string
      assignedCalendarGuid: string
    }
  | {
      type: typeof AutoChatReplySettingExtraType.None
    }

type ExtraType = Extra['type']

export type AutoChatReplySettingTableForm = {
  keyword: string
  body: string
  isEnabled: boolean
  extra: Extra
}

export default {}
</script>

<script lang="ts" setup>
import ButtonIconOnly from '@/components/atoms/ButtonIconOnly.vue'
import { useCalendar } from '@/hooks/useCalendar.ts'
import DolphinRadioButton from '@/components/atoms/DolphinRadioButton.vue'
import DolphinSelector from '@/components/atoms/DolphinSelector.vue'
import DolphinTextInput from '@/components/atoms/DolphinTextInput.vue'
import DolphinTextarea from '@/components/atoms/DolphinTextarea.vue'
import FlatButton from '@/components/atoms/FlatButton.vue'
import IconTrash from '@/components/icons/IconTrash.vue'

import MessageAttachmentActions from '@/components/organisms/MessageAttachmentActions/MessageAttachmentActions.vue'
import { assertExhaustive } from '@/libs/utilities/assertExhaustive'
import SettingsTable from '@/pages/SettingsPage/SettingsTable/SettingsTable.vue'
import type { Option, OptionValueType } from '@/types/app'
import {
  AutoChatReplySettingExtraType,
  ExtraBookingRequestAvailableTimeSlotRange,
} from '@/types/schema.generated'
import cloneDeep from 'clone-deep'
import { computed, ref, watch } from 'vue'
import { useAvailableCalendarsQuery } from './AnswerMessageAutoChatReplySettingTable.generated'
import AnswerMessageAutoChatReplySettingTableInformation from './AnswerMessageAutoChatReplySettingTableInformation.vue'
import AnswerMessageAutoChatReplySettingTableRow from './AnswerMessageAutoChatReplySettingTableRow.vue'
import { RouterLink } from 'vue-router'

const emit = defineEmits<{
  (event: 'save', data: AutoChatReplySettingTableForm): void
}>()

const props = defineProps<{
  form: AutoChatReplySettingTableForm
}>()

const isEnabledOptions: Option<boolean>[] = [
  {
    value: true,
    text: '有効',
  },
  {
    value: false,
    text: '無効',
  },
]

const extraOptions: Array<Option<ExtraType>> = [
  {
    value: AutoChatReplySettingExtraType.Question,
    text: '選択肢',
  },
  {
    value: AutoChatReplySettingExtraType.BookingRequest,
    text: '日程調整',
  },
  {
    value: AutoChatReplySettingExtraType.None,
    text: 'なし',
  },
]

const bookingRequestAvailableTimeSlotRangeOptionGroups = computed<
  Array<Array<Option<ExtraBookingRequestAvailableTimeSlotRange>>>
>(() => {
  return [
    [
      {
        value: ExtraBookingRequestAvailableTimeSlotRange.Anytime,
        text: '直近の空いている時間',
      },
    ],
    [
      {
        value: ExtraBookingRequestAvailableTimeSlotRange.WeekdayAnytime,
        text: '平日 終日',
      },
      {
        value: ExtraBookingRequestAvailableTimeSlotRange.WeekdayDaytime,
        text: '平日 日中',
      },
      {
        value: ExtraBookingRequestAvailableTimeSlotRange.WeekdayNight,
        text: '平日 夕方以降',
      },
    ],
    [
      {
        value: ExtraBookingRequestAvailableTimeSlotRange.WeekendAnytime,
        text: '土日 終日',
      },
      {
        value: ExtraBookingRequestAvailableTimeSlotRange.WeekendDaytime,
        text: '土日 日中',
      },
      {
        value: ExtraBookingRequestAvailableTimeSlotRange.WeekendNight,
        text: '土日 夕方以降',
      },
    ],
  ]
})

const initialForm = cloneDeep(props.form)

const formRef = ref<AutoChatReplySettingTableForm>(initialForm)

watch(formRef.value, value => {
  if (value.body || value.keyword) {
    validateErrors()
  }
})

const updateExtra = (v: OptionValueType) => {
  switch (v) {
    case AutoChatReplySettingExtraType.Question:
      const selections =
        initialForm.extra.type === AutoChatReplySettingExtraType.Question
          ? initialForm.extra.selections
          : ['', '']

      formRef.value = {
        ...formRef.value,
        extra: {
          type: AutoChatReplySettingExtraType.Question,
          selections: selections,
        },
      }
      break
    case AutoChatReplySettingExtraType.BookingRequest:
      const fallbackUrl =
        initialForm.extra.type === AutoChatReplySettingExtraType.BookingRequest
          ? initialForm.extra.fallbackUrl
          : ''

      const assignedCalendarGuid =
        initialForm.extra.type === AutoChatReplySettingExtraType.BookingRequest
          ? initialForm.extra.assignedCalendarGuid
          : ''

      const availableTimeSlotRange =
        initialForm.extra.type === AutoChatReplySettingExtraType.BookingRequest
          ? initialForm.extra.availableTimeSlotRange
          : ExtraBookingRequestAvailableTimeSlotRange.Anytime

      formRef.value = {
        ...formRef.value,
        extra: {
          type: AutoChatReplySettingExtraType.BookingRequest,
          availableTimeSlotRange,
          fallbackUrl: fallbackUrl,
          assignedCalendarGuid: assignedCalendarGuid,
        },
      }
      break
    case AutoChatReplySettingExtraType.None:
      formRef.value = {
        ...formRef.value,
        extra: {
          type: AutoChatReplySettingExtraType.None,
        },
      }
      break
  }

  formErrors.value = {
    ...formErrors.value,
    extra: undefined,
  }
}

const updateBookingRequestAvailableTimeSlotRange = (v: OptionValueType) => {
  if (typeof v !== 'string') {
    return
  }

  const { extra } = formRef.value

  if (extra.type === AutoChatReplySettingExtraType.BookingRequest) {
    formRef.value = {
      ...formRef.value,
      extra: {
        ...extra,
        availableTimeSlotRange: v as ExtraBookingRequestAvailableTimeSlotRange,
      },
    }
  }
}

// 外部カレンダー連携している人が最低一人いることを確認
const { isCalendarAvailable } = useCalendar()

const availableCalendarsQuery = useAvailableCalendarsQuery()
const calendarOptions = computed(() => {
  const calendars =
    availableCalendarsQuery.result.value?.currentEntrance.calendarSummaries

  if (!calendars) {
    return []
  }

  return calendars
    .filter(calendar => calendar.status === 'ACTIVE')
    .map(calendar => ({
      value: calendar.guid,
      text: calendar.privateTitle
        ? calendar.publicTitle + ` (${calendar.privateTitle})`
        : calendar.publicTitle,
    }))
})

const clearCalendarSelection = () => {
  if (
    formRef.value.extra.type === AutoChatReplySettingExtraType.BookingRequest
  ) {
    formRef.value.extra.assignedCalendarGuid = ''
  }
}

const submitting = ref(false)

const saveSetting = async () => {
  submitting.value = true

  validateErrors()
  const hasError = Object.values(formErrors.value).some(
    error => error !== undefined
  )
  if (hasError) {
    submitting.value = false
    return
  }

  const submittedForm: AutoChatReplySettingTableForm = {
    ...formRef.value,
  }

  emit('save', submittedForm)
  submitting.value = false
}

const validateErrors = () => {
  formErrors.value = {}

  if (!formRef.value.body) {
    formErrors.value.body = 'メッセージ内容を入力してください'
  }
  if (!formRef.value.keyword) {
    formErrors.value.keyword = '自動応答する選択肢を入力してください'
  }

  formErrors.value = {
    ...formErrors.value,
    ...validateExtra(formRef.value),
  }
}

function validateExtra(
  form: AutoChatReplySettingTableForm
): Record<string, string | undefined> {
  const extra = form.extra

  switch (extra.type) {
    case AutoChatReplySettingExtraType.Question:
      if (extra.selections.filter(Boolean).length < 2) {
        return {
          extraSelections: '選択肢を2つ以上入力してください',
        }
      }

      return {}
    case AutoChatReplySettingExtraType.BookingRequest:
      const errors: Record<string, string> = {}

      if (extra.fallbackUrl) {
        try {
          new URL(extra.fallbackUrl)
        } catch (e) {
          errors['extraFallbackUrl'] = '有効なURLを入力してください'
        }
      }

      if (!extra.availableTimeSlotRange) {
        errors['extraAvailableTimeSlotRange'] = 'いずれかを選択してください'
      }

      return errors
    case AutoChatReplySettingExtraType.None:
      return {}
    default:
      assertExhaustive(extra)
  }
}

const formErrors = ref<Record<string, string | undefined>>({})
</script>

<style lang="scss" module>
.container {
  &__footer {
    &:nth-child(n + 2) {
      padding-top: 24px;
      border-top: 1px solid $color-border-low-emphasis;
      margin-top: 40px;
    }
  }
}

.table {
  display: grid;
  row-gap: 40px;
}

.textInput {
  width: 100%;
}

.explanation {
  align-items: center;
  color: $color-text-and-object-high-emphasis;
  font-size: 12px;

  &__keyword {
    font-weight: bold;
  }
}

.options {
  $gutter-horizontal: 40px;
  $gutter-vertical: 24px;

  display: flex;
  flex-wrap: wrap;
  margin-top: -$gutter-vertical;
  margin-left: -$gutter-horizontal;

  &--bookingRequest {
    margin: -16px -40px 0;
  }

  &__item {
    margin-top: $gutter-vertical;
    margin-left: $gutter-horizontal;
  }
}

.option {
  display: inline-flex;
  align-items: center;

  &__text {
    font-size: 14px;

    &:nth-child(n + 2) {
      margin-left: 4px;
    }
  }
}

.extra {
  margin-top: 16px;
}

.bookingRequest {
  display: block;

  &__input {
    width: 100%;
    margin-bottom: 4px;
  }

  &__selectInput {
    width: 100%;
    margin-bottom: 4px;
  }

  &__label {
    padding: 4px;
    margin-bottom: 4px;
    font-weight: 700;
    font-size: 14px;

    &:nth-child(n + 2) {
      padding-top: 16px;
    }
  }

  &__note {
    font-size: 10px;
    color: $color-text-and-object-low-emphasis;

    &::before {
      content: '*';
      font-size: 10px;
      margin-right: 0.5em;
    }
  }

  &__info {
    margin-top: 16px;
  }

  &__warning {
    font-size: 12px;
    font-weight: bold;
    color: $color-caution-accent;
  }
}

.saveWrapper {
  display: flex;
  margin-top: 24px;
  justify-content: center;
}

.error {
  color: $color-caution-accent;
  font-weight: 700;
  margin-top: 8px;
}

.calendarSelector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.deleteButton {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 36px;
  height: 36px;
  border-radius: 4px;
  border: 1px solid $color-caution-accent;
  background-color: $color-surface-primary;
  color: $color-caution-accent;
  font-size: 16px;
  cursor: pointer;

  &:hover {
    background-color: $color-surface-secondary;
  }
}
</style>
